// frontend/src/components/Schedules/BlockScheduleList.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function BlockScheduleList() {
  const [blockSchedules, setBlockSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [durationCache, setDurationCache] = useState({});

  const fetchSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/schedules/api/block-schedules/');
      const schedules = Array.isArray(response.data) ? response.data : [];
      setBlockSchedules(schedules);
      
      // Calculate holiday-aware duration for each schedule
      const newDurationCache = {};
      for (const schedule of schedules) {
        if (schedule.start_date && schedule.end_date) {
          try {
            const durationResponse = await axios.get('/schedules/api/block-schedules/calculate-duration/', {
              params: {
                start_date: schedule.start_date,
                end_date: schedule.end_date
              }
            });
            newDurationCache[schedule.id] = {
              calendar_weeks: durationResponse.data.calendar_weeks,
              actual_school_weeks: durationResponse.data.actual_school_weeks,
              holidays: durationResponse.data.holidays
            };
          } catch (durationErr) {
            console.error(`Error calculating duration for schedule ${schedule.id}:`, durationErr);
            // Fallback to simple calculation
            const weeks = computeWeeksFromDates(schedule.start_date, schedule.end_date);
            newDurationCache[schedule.id] = {
              calendar_weeks: parseInt(weeks) || 0,
              actual_school_weeks: parseInt(weeks) || 0,
              holidays: []
            };
          }
        }
      }
      setDurationCache(newDurationCache);
    } catch (err) {
      console.error('Error fetching block schedules:', err);
      setError('Failed to load block schedules.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this block schedule? This cannot be undone.')) return;
    try {
      await axios.delete(`/schedules/api/block-schedules/${id}/`);
      setBlockSchedules((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error('Error deleting block schedule:', err);
      alert('Failed to delete.');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return d;
    }
  };

  const computeWeeksFromDates = (start, end) => {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return '';
    const diff = e - s;
    if (diff < 0) return '';
    const oneDay = 24 * 60 * 60 * 1000;
    const oneWeek = 7 * oneDay;
    return String(Math.max(1, Math.ceil((diff + oneDay) / oneWeek)));
  };

  const computeWeeksFromDays = (days) => {
    if (days === null || days === undefined) return '';
    const d = Number(days);
    if (Number.isNaN(d) || d < 0) return '';
    // Backend stores duration in days (exclusive of end), make it inclusive by +1
    return String(Math.max(1, Math.ceil((d + 1) / 7)));
  };

  return (
    <div className="container">
      <h1>Block Schedules</h1>
      <p>View and manage time-based blocks used for scheduling throughout the year.</p>

      <div className="actions-row">
        <Link to="/schedules/block-schedules/add" className="primary-btn">+ Add Block Schedule</Link>
        <button className="secondary-btn" onClick={fetchSchedules}>Refresh</button>
      </div>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {!loading && !error && (
        <div className="card-grid">
          {blockSchedules.length > 0 ? (
            blockSchedules.map((bs) => (
              <div key={bs.id} className="card">
                <div className="card-header">
                  <h3>{bs.name}</h3>
                  {(() => {
                    const durationData = durationCache[bs.id];
                    if (durationData) {
                      return (
                        <div>
                          <span className="badge primary">{durationData.actual_school_weeks} weeks</span>
                          {durationData.calendar_weeks !== durationData.actual_school_weeks && (
                            <span className="badge secondary" style={{ marginLeft: '4px' }}>
                              ({durationData.calendar_weeks} calendar)
                            </span>
                          )}
                        </div>
                      );
                    }
                    // Fallback to old calculation while loading
                    const weeks = bs?.duration !== undefined && bs?.duration !== null
                      ? computeWeeksFromDays(bs.duration)
                      : computeWeeksFromDates(bs?.start_date, bs?.end_date);
                    return weeks ? <span className="badge">{weeks} weeks</span> : null;
                  })()}
                </div>
                <div className="card-body">
                  <p>
                    <strong>Dates:</strong> {formatDate(bs.start_date)} - {formatDate(bs.end_date)}
                  </p>
                  {durationCache[bs.id]?.holidays?.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <p style={{ fontSize: '0.85rem', color: '#666', margin: '4px 0' }}>
                        <strong>Holidays:</strong> {durationCache[bs.id].holidays.length} holiday(s) excluded
                      </p>
                    </div>
                  )}
                </div>
                <div className="card-footer">
                  {/* Placeholder for future edit route */}
                  {/* <Link to={`/schedules/block-schedules/edit/${bs.id}`} className="link-btn">Edit</Link> */}
                  <button className="danger-btn" onClick={() => handleDelete(bs.id)}>Delete</button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No block schedules found.</p>
              <Link to="/schedules/block-schedules/add" className="primary-btn">Create your first block schedule</Link>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .actions-row { display: flex; gap: 12px; margin: 16px 0 24px; }
        .primary-btn, .secondary-btn, .danger-btn, .link-btn {
          padding: 10px 14px; border-radius: 8px; text-decoration: none; cursor: pointer; border: 1px solid transparent;
        }
        .primary-btn { background: #1976d2; color: #fff; }
        .secondary-btn { background: #fff; color: #1976d2; border-color: #1976d2; }
        .danger-btn { background: #e53935; color: #fff; }
        .link-btn { background: transparent; color: #1976d2; }

        .card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; }
        .card { background: #fff; border: 1px solid #e0e0e0; border-radius: 12px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
        .badge { border-radius: 999px; padding: 4px 10px; font-size: 0.85rem; }
        .badge.primary { background: #e3f2fd; color: #1976d2; }
        .badge.secondary { background: #f5f5f5; color: #666; }
        .card-footer { display: flex; gap: 8px; margin-top: 12px; }
        .empty-state { text-align: center; padding: 24px; border: 1px dashed #ccc; border-radius: 12px; background: #fafafa; }
      `}</style>
    </div>
  );
}

export default BlockScheduleList;
